import type { Message } from '../types';

// Newest-first, with an id tiebreak so two messages sharing a timestamp
// (e.g. rapid votes) always land in the same relative order no matter how
// many times the same data is re-merged.
export function compareMessages(a: Message, b: Message): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

// Upserts `incoming` into a newest-first list by id and returns a NEW array.
//
// `resolve(existing, incoming)` picks the stored value when an id already
// exists (the store uses it to keep an optimistic message's local sync
// state); default is "incoming wins".
//
// A live stream delivers one or a few signals at a time, so small batches are
// placed by binary search instead of rebuilding a Map and re-sorting every
// message on every signal; a page-sized batch takes the Map + sort path.
export function upsertMessages(
  list: readonly Message[],
  incoming: readonly Message[],
  resolve: (existing: Message | undefined, incoming: Message) => Message = (_e, i) => i
): Message[] {
  if (incoming.length === 0) return list as Message[];
  if (incoming.length > 32) {
    const byId = new Map<string, Message>();
    for (const m of list) byId.set(m.id, m);
    for (const m of incoming) byId.set(m.id, resolve(byId.get(m.id), m));
    return [...byId.values()].sort(compareMessages);
  }
  const out = list.slice();
  for (const raw of incoming) {
    const at = out.findIndex((m) => m.id === raw.id);
    const existing = at >= 0 ? out[at] : undefined;
    const next = resolve(existing, raw);
    if (existing && existing.date === next.date) {
      out[at] = next;
      continue;
    }
    if (at >= 0) out.splice(at, 1);
    // First index that should come AFTER `next` (list is newest-first).
    let lo = 0;
    let hi = out.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (compareMessages(out[mid], next) <= 0) lo = mid + 1;
      else hi = mid;
    }
    out.splice(lo, 0, next);
  }
  return out;
}
